import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ApiService } from '../api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectConfiguration, ProjectId } from '@models/ProjectConfiguration';
import { FormArray, FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { SynchronizerService } from '../synchronizer.service';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest } from 'rxjs';
import { PublicationConfigurationListComponent } from './publication-configuration-list/publication-configuration-list.component'
import { Canon } from '@models/Canon';
import { UbsBook } from '@models/UbsBook';
import { GeneralComponent } from './general/general.component';
import { CanonsComponent } from "./canons/canons.component";
import { ParsingFormatsComponent } from "./parsing-formats/parsing-formats.component";
import { ProjectMembersComponent } from "./project-members/project-members.component";
import { ConfirmationDialogData, ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { CanComponentDeactivate } from '../can-deactivate.guard';
import { ParsingFormat } from '@models/parsing-formats/ParsingFormat';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-project-settings',
  imports: [CommonModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatTabsModule,
    FormsModule,
    PublicationConfigurationListComponent,
    GeneralComponent,
    CanonsComponent,
    ParsingFormatsComponent,
    ProjectMembersComponent,
    MatIconModule
  ],
  templateUrl: './project-settings.component.html',
  styleUrl: './project-settings.component.scss'
})
export class ProjectSettingsComponent implements OnInit, CanComponentDeactivate {
  @Output() resetEvent = new EventEmitter<void>();

  public project: ProjectConfiguration | undefined;
  form: FormGroup;
  id: ProjectId = '';
  bIsNewProject: boolean = false;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private api: ApiService,
    private synchronizerService: SynchronizerService,
    public dialog: MatDialog,
    private route: ActivatedRoute) {

    this.form = this.fb.group({
      general: this.fb.group({}),
      canons: this.fb.group({}),
      parsingFormats: this.fb.group({}),
      publicationConfigurations: this.fb.array([]),
      projectMembers: this.fb.array([])
    });
  }

  get fgGeneral(): FormGroup {
    return this.form.get('general') as FormGroup;
  }

  get fgCanons(): FormGroup {
    return this.form.get('canons') as FormGroup;
  }

  get fgParsingFormats(): FormGroup {
    return this.form.get('parsingFormats') as FormGroup;
  }

  get fgPublicationConfigurations(): FormArray {
    return this.form.get('publicationConfigurations') as FormArray;
  }

  get fgProjectMembers(): FormArray {
    return this.form.get('projectMembers') as FormArray;
  }

  ngOnInit() {
    combineLatest([this.route.paramMap, this.synchronizerService.user$]).subscribe(([params, user]) => {
      if (user) {
        /// if a project has been passed as an @Input
        if (this.project === undefined && params.has('projectid')) {
          this.id = params.get('projectid') as ProjectId;
          this.loadDataFromUserData();
        }
      }
    });

  }

  resetData() {
    const data: ConfirmationDialogData = {
      title: `Confirm discarding changes`,
      message: `Are you sure you want to lose the changes you've made to the settings?`,
      yes: 'Discard My Changes',
      no: 'Dismiss'
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: data
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.resetEvent.emit();
      }
    });
  }

  loadDataFromUserData() {
    const p = this.synchronizerService.user$.value?.project(this.id);
    if (p) {
      /// if p is defined, that means we're editing an existing project
      this.project = ProjectConfiguration.fromRow(p.toObject())
    } else {
      if (this.synchronizerService.user$.value) {
        /// if p is not defined, that means we're creating a new project
        this.project = new ProjectConfiguration(this.id);
        this.project.setMember({ user_id: this.synchronizerService.user$.value?.user_id, user_role: 'admin', power_user: 0 });
        this.bIsNewProject = true;
      } else {
        console.error("No user data available to create a new project. (I don't expect this to be possible.)");
      }
    }
  }

  async save() {
    if (!this.form.valid) {
      return;
    }

    const p = new ProjectConfiguration(this.id);

    p.title = this.fgGeneral.get('project_title')?.value;
    p.layout_direction = this.fgGeneral.get('layout_direction')?.value;
    p.description = this.fgGeneral.get('project_description')?.value;
    p.allow_joins = this.fgGeneral.get('allow_joins')?.value;
    p.font_families = this.fgGeneral.get('font_families')?.value;
    p.font_size = this.fgGeneral.get('font_size')?.value || undefined;
    p.numerals = this.fgGeneral.get('numerals')?.value.split(',');
    p.glossSuggestionMode = this.fgGeneral.get('gloss_suggestion_mode')?.value || 'byBinyanOrVoice';

    /// update the frequency thresholds
    p.canons = this.fgCanons.get('canons')?.value;

    const thresholds = new Map<Canon, number>();
    p.canons.forEach(canon => {
      if (this.fgCanons.get(canon as string)) {
        thresholds.set(canon, this.fgCanons.get(canon as string)?.value);
      }
    });
    p.frequency_thresholds = thresholds;

    /// update the book names
    const bookNames = new Map<UbsBook, string>();
    for (const b of p.bookNames.keys()) {
      if (this.fgCanons.get(b as string)) {
        bookNames.set(b, this.fgCanons.get(b as string)?.value);
      }
    }
    p.bookNames = bookNames;

    /// set the parsing formats
    Object.keys(this.fgParsingFormats.controls).forEach((key) => {
      const c = key as Canon;
      this.fgParsingFormats.get(c)?.value.forEach((pf: ParsingFormat) => {
        p.parsingFormats.setParsingFormat(c, pf.id, pf);
      });
    });

    /// set the publication configurations
    this.fgPublicationConfigurations.controls.forEach((control) => {
      const publicationConfig = control.value;
      if (publicationConfig.id) {
        p.publicationConfigurations.set(publicationConfig.id, publicationConfig);
      }
    });

    /// set the project members
    this.fgProjectMembers.controls.forEach((control) => {
      const projectMember = control.value;
      projectMember.power_user = projectMember.power_user ? 1 : 0;
      p.setMember(projectMember);
    });

    // Save the updated project configuration using the database service
    const result = await this.api.saveProjectConfiguration(p, this.bIsNewProject);
    console.log("Project saved", result);

    /// the if is just to keep Typescript happy
    if (this.synchronizerService.user$.value) {
      const user = await this.api.getUserData(this.synchronizerService.user$.value.user_id)
      this.synchronizerService.user$.next(user);
    }

    this.router.navigate(['/myprojects']);
  }

  async canDeactivate(): Promise<boolean> {
    if (this.form.dirty) {
      const data: ConfirmationDialogData = {
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Do you really want to leave this page and lose your changes?',
        yes: 'Leave',
        no: 'Stay',
      };

      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '350px',
        data: data,
      });

      const result = await dialogRef.afterClosed().toPromise();
      return result === true; // Allow navigation if the user confirms
    }

    return true; // Allow navigation if the form is not dirty
  }

  get userIsNotAdmin(): boolean {
    if (this.synchronizerService.user$.value) {
      const user = this.synchronizerService.user$.value;
      const project = this.synchronizerService.user$.value?.project(this.id);
      if (project) {
        return project.userRole(user.user_id) !== 'admin';
      }
    }
    return true;
  }

}
